'use client';

import React, { useState, useEffect } from 'react';
import {
  Send,
  X,
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronDown,
  ChevronRight,
  Tv2,
  Clapperboard,
  Webhook,
  Package,
} from 'lucide-react';
import { toast } from 'sonner';
import type { MediaItem, WebhookConfig, SonarrInstance, RadarrInstance } from '@/lib/types';
import { buildPayload } from '@/lib/payloadBuilder';
import { configStore } from '@/lib/configStore';
import { webhookLogStore } from '@/lib/webhookLogStore';

interface Props {
  selectedItems: MediaItem[];
  onClose: () => void;
  onComplete: () => void;
}

type DispatchStatus = 'idle' | 'dispatching' | 'done';
type ItemStatus = 'pending' | 'sending' | 'success' | 'error';

interface ItemDispatchState {
  id: string;
  status: ItemStatus;
  error?: string;
}

export default function DispatchModal({ selectedItems, onClose, onComplete }: Props) {
  const [webhooks, setWebhooks] = useState<WebhookConfig[]>([]);
  const [sonarrInstances, setSonarrInstances] = useState<SonarrInstance[]>([]);
  const [radarrInstances, setRadarrInstances] = useState<RadarrInstance[]>([]);
  const [selectedWebhookId, setSelectedWebhookId] = useState('');
  const [dispatchStatus, setDispatchStatus] = useState<DispatchStatus>('idle');
  const [itemStates, setItemStates] = useState<Record<string, ItemDispatchState>>(() =>
    Object.fromEntries(
      selectedItems.map((i) => [i.id, { id: i.id, status: 'pending' as ItemStatus }])
    )
  );
  const [expandedPayload, setExpandedPayload] = useState<string | null>(null);

  useEffect(() => {
    const whs = configStore.getWebhooks();
    const sonarr = configStore.getSonarrInstances();
    const radarr = configStore.getRadarrInstances();
    setWebhooks(whs);
    setSonarrInstances(sonarr);
    setRadarrInstances(radarr);
    if (whs.length > 0) setSelectedWebhookId(whs[0].id);
  }, []);

  const selectedWebhook = webhooks.find((w) => w.id === selectedWebhookId);

  const getPayloadFieldsForItem = (item: MediaItem) => {
    if (item.mediaType === 'series') {
      const inst = sonarrInstances.find((s) => s.id === item.sourceInstanceId) ?? sonarrInstances[0];
      return inst?.payloadFields ?? [];
    }
    if (item.mediaType === 'movie') {
      const inst = radarrInstances.find((r) => r.id === item.sourceInstanceId) ?? radarrInstances[0];
      return inst?.payloadFields ?? [];
    }
    return [];
  };

  const successCount = Object.values(itemStates).filter((s) => s.status === 'success').length;
  const errorCount = Object.values(itemStates).filter((s) => s.status === 'error').length;
  const allDone = successCount + errorCount === selectedItems.length && dispatchStatus === 'dispatching';

  useEffect(() => {
    if (allDone) {
      setDispatchStatus('done');
      if (errorCount === 0) {
        toast.success(`${successCount} item${successCount !== 1 ? 's' : ''} dispatched successfully`);
      } else {
        toast.warning(`${successCount} dispatched, ${errorCount} failed`);
      }
    }
  }, [allDone, successCount, errorCount]);

  const handleDispatch = async () => {
    if (!selectedWebhook) {
      toast.error('No webhook configured. Add one in Settings first.');
      return;
    }
    setDispatchStatus('dispatching');
    setItemStates(
      Object.fromEntries(
        selectedItems.map((i) => [i.id, { id: i.id, status: 'pending' as ItemStatus }])
      )
    );

    const logItems: import('@/lib/types').WebhookLogItem[] = [];

    for (const item of selectedItems) {
      setItemStates((prev) => ({
        ...prev,
        [item.id]: { id: item.id, status: 'sending' },
      }));

      // BACKEND INTEGRATION: Replace with actual fetch to selectedWebhook.url
      await new Promise((resolve) => setTimeout(resolve, 300 + Math.floor(item.id.charCodeAt(item.id.length - 1) * 3.7) % 400));

      const success = item.id.charCodeAt(item.id.length - 1) % 10 !== 0;
      const errorMsg = success ? undefined : 'Connection refused — webhook endpoint unreachable';

      setItemStates((prev) => ({
        ...prev,
        [item.id]: {
          id: item.id,
          status: success ? 'success' : 'error',
          error: errorMsg,
        },
      }));

      const payloadFields = getPayloadFieldsForItem(item);
      const payload = buildPayload(item, selectedWebhook?.name ?? '', payloadFields);
      logItems.push({
        id: item.id,
        title: item.title,
        mediaType: item.mediaType,
        sourceInstanceName: item.sourceInstanceName,
        status: success ? 'success' : 'error',
        error: errorMsg,
        payload: payload as unknown as Record<string, unknown>,
      });
    }

    // Persist log entry
    const successCount = logItems.filter((i) => i.status === 'success').length;
    const errorCount = logItems.filter((i) => i.status === 'error').length;
    webhookLogStore.addLog({
      id: `log-${Date.now()}`,
      timestamp: new Date().toISOString(),
      webhookName: selectedWebhook?.name ?? '',
      webhookUrl: selectedWebhook?.url ?? '',
      connectionSource: logItems.map((i) => i.sourceInstanceName ?? 'Custom').filter((v, i, a) => a.indexOf(v) === i).join(', '),
      itemCount: logItems.length,
      status: errorCount === 0 ? 'success' : successCount === 0 ? 'failed' : 'partial',
      successCount,
      errorCount,
      items: logItems,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={dispatchStatus === 'idle' ? onClose : undefined}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg card-elevated fade-in flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Send size={16} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Dispatch to Webhook</h2>
              <p className="text-xs text-muted-foreground">
                {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} · one payload each
              </p>
            </div>
          </div>
          {dispatchStatus !== 'dispatching' && (
            <button onClick={onClose} className="btn-ghost p-1.5 rounded-lg">
              <X size={16} />
            </button>
          )}
        </div>

        {/* Webhook selector */}
        {dispatchStatus === 'idle' && (
          <div className="p-5 border-b border-border">
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
              Target Webhook
            </label>
            {webhooks.length === 0 ? (
              <div className="flex items-center gap-2 p-3 rounded-lg border border-dashed border-border text-sm text-muted-foreground">
                <Webhook size={15} />
                No webhooks configured. Add one in Settings.
              </div>
            ) : (
              <select
                value={selectedWebhookId}
                onChange={(e) => setSelectedWebhookId(e.target.value)}
                className="input-base w-full px-3 py-2 text-sm"
              >
                {webhooks.map((wh) => (
                  <option key={`wh-select-${wh.id}`} value={wh.id}>
                    {wh.name} — {wh.url.length > 40 ? wh.url.slice(0, 40) + '...' : wh.url}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Item list */}
        <div className="flex-1 overflow-y-auto scrollbar-thin p-5 flex flex-col gap-2">
          {selectedItems.map((item) => {
            const state = itemStates[item.id];
            const payloadFields = getPayloadFieldsForItem(item);
            const payload = buildPayload(item, selectedWebhook?.name ?? '', payloadFields);
            const payloadStr = JSON.stringify(payload, null, 2);
            const isExpanded = expandedPayload === item.id;

            return (
              <div
                key={`dispatch-item-${item.id}`}
                className="rounded-lg border border-border overflow-hidden"
                style={{ background: 'var(--input)' }}
              >
                <div className="flex items-center gap-3 px-3 py-2.5">
                  {/* Source icon */}
                  {item.mediaType === 'series' ? (
                    <Tv2 size={14} className="text-sonarr flex-shrink-0" />
                  ) : item.mediaType === 'movie' ? (
                    <Clapperboard size={14} className="text-radarr flex-shrink-0" />
                  ) : (
                    <Package size={14} className="text-muted-foreground flex-shrink-0" />
                  )}

                  {/* Title + source */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{item.title}</p>
                    <p className="text-2xs text-muted-foreground font-mono-data">
                      {item.year} · {item.status}
                      {item.sourceInstanceName && (
                        <span className="ml-1 opacity-60">· {item.sourceInstanceName}</span>
                      )}
                    </p>
                  </div>

                  {/* Status indicator */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {state.status === 'pending' && (
                      <span className="text-2xs text-muted-foreground">Pending</span>
                    )}
                    {state.status === 'sending' && (
                      <Loader2 size={14} className="text-primary animate-spin" />
                    )}
                    {state.status === 'success' && (
                      <CheckCircle2 size={16} className="text-success" />
                    )}
                    {state.status === 'error' && (
                      <XCircle size={16} className="text-danger" />
                    )}

                    {/* Payload toggle */}
                    <button
                      onClick={() => setExpandedPayload(isExpanded ? null : item.id)}
                      className="btn-ghost p-1 rounded"
                      title="Preview JSON payload"
                    >
                      {isExpanded ? (
                        <ChevronDown size={13} className="text-muted-foreground" />
                      ) : (
                        <ChevronRight size={13} className="text-muted-foreground" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Error message */}
                {state.status === 'error' && state.error && (
                  <div className="px-3 pb-2">
                    <p className="text-2xs text-danger font-mono-data">{state.error}</p>
                  </div>
                )}

                {/* Payload preview */}
                {isExpanded && (
                  <div className="border-t border-border px-3 py-2">
                    {payloadFields.length > 0 && (
                      <p className="text-2xs text-muted-foreground mb-1.5">
                        <span className="text-primary font-semibold">{payloadFields.filter(f => f.enabled).length}</span> custom fields from {item.sourceInstanceName ?? 'instance'} applied
                      </p>
                    )}
                    <pre className="text-2xs text-muted-foreground font-mono-data overflow-x-auto scrollbar-thin leading-relaxed">
                      {payloadStr}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-border flex items-center justify-between gap-3">
          {dispatchStatus === 'done' ? (
            <>
              <div className="flex items-center gap-2">
                {errorCount === 0 ? (
                  <CheckCircle2 size={16} className="text-success" />
                ) : (
                  <XCircle size={16} className="text-danger" />
                )}
                <span className="text-sm font-medium text-foreground">
                  {successCount} sent
                  {errorCount > 0 && (
                    <span className="text-danger ml-1">· {errorCount} failed</span>
                  )}
                </span>
              </div>
              <button onClick={onComplete} className="px-5 py-2 text-sm font-semibold btn-primary">
                Done
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                disabled={dispatchStatus === 'dispatching'}
                className="px-4 py-2 text-sm font-medium btn-ghost disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={handleDispatch}
                disabled={dispatchStatus === 'dispatching' || webhooks.length === 0}
                className="flex items-center gap-2 px-5 py-2 text-sm font-semibold btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {dispatchStatus === 'dispatching' ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Dispatching...
                  </>
                ) : (
                  <>
                    <Send size={14} />
                    Send {selectedItems.length} Payload{selectedItems.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}